import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import Navbar from './components/Navbar';
import Dashboard from './pages/Dashboard';
import ExpenseTracker from './pages/ExpenseTracker';
import MonthlyBills from './pages/MonthlyBills';
import IncomeTracker from './pages/IncomeTracker';
import TaxForms from './pages/TaxForms';
import PropertyTaxQuestionnaire from './pages/PropertyTaxQuestionnaire';
import RothIRAPlanner from './pages/RothIRAPlanner';
import PropertyManager from './pages/PropertyManager';
import MortgageCalculator from './pages/MortgageCalculator';
import AccountManager from './pages/AccountManager';
import TaxableAccounts from './pages/TaxableAccounts';
import IndividualStockAnalysis from './pages/IndividualStockAnalysis';
import BulkStockAnalysis from './pages/BulkStockAnalysis';
import AutomatedTrader from './pages/AutomatedTrader';
import Login from './pages/Login';
import Register from './pages/Register';
import LandingPage from './pages/LandingPage';
import AboutPage from './pages/AboutPage';
import HelpPage from './pages/HelpPage';
import PlatformPage from './pages/PlatformPage';
import ExamplesPage from './pages/ExamplesPage';
import TermsPage from './pages/TermsPage';
import PrivacyPage from './pages/PrivacyPage';
import LegalPage from './pages/LegalPage';
import AdminPanel from './pages/AdminPanel';
import NotFound from './pages/NotFound';
import ContractSignature from './components/ContractSignature';
import DemoBanner from './components/DemoBanner';
import NoIndex from './components/NoIndex';
import './styles/App.css';

// Protected Route Component (adds noindex - these pages redirect to login when crawled)
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading WealthCore...</p>
        </div>
      </div>
    );
  }
  
  return isAuthenticated ? (
    <>
      <NoIndex />
      {children}
    </>
  ) : (
    <Navigate to="/login" replace />
  );
};

// Public Route Component
const PublicRoute = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading WealthCore...</p>
        </div>
      </div>
    );
  }
  
  return !isAuthenticated ? children : <Navigate to="/dashboard" replace />;
};

function AppRoutes() {
  return (
    <Router
      future={{
        v7_startTransition: true,
        v7_relativeSplatPath: true,
      }}
    >
      <div className="min-h-screen bg-gray-50">
        <Navbar />
        <div className="pt-16">
          <DemoBanner />
          <main>
          <Routes>
            {/* Public Routes */}
            <Route path="/" element={<LandingPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/help" element={<HelpPage />} />
            <Route path="/platform" element={<PlatformPage />} />
            <Route path="/examples" element={<ExamplesPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/legal" element={<LegalPage />} />
            <Route path="/login" element={
              <PublicRoute>
                <Login />
              </PublicRoute>
            } />
            <Route path="/register" element={
              <PublicRoute>
                <Register />
              </PublicRoute>
            } />
            
            {/* Protected Routes */}
            <Route path="/dashboard" element={
              <ProtectedRoute>
                <Dashboard />
              </ProtectedRoute>
            } />
            <Route path="/expense-tracker" element={
              <ProtectedRoute>
                <ExpenseTracker />
              </ProtectedRoute>
            } />
            <Route path="/monthly-bills" element={
              <ProtectedRoute>
                <MonthlyBills />
              </ProtectedRoute>
            } />
            <Route path="/income-tracker" element={
              <ProtectedRoute>
                <IncomeTracker />
              </ProtectedRoute>
            } />
            <Route path="/tax-forms" element={
              <ProtectedRoute>
                <TaxForms />
              </ProtectedRoute>
            } />
            <Route path="/property-tax-questionnaire" element={
              <ProtectedRoute>
                <PropertyTaxQuestionnaire />
              </ProtectedRoute>
            } />
            <Route path="/roth-ira-planner" element={
              <ProtectedRoute>
                <RothIRAPlanner />
              </ProtectedRoute>
            } />
            <Route path="/property-manager" element={<ProtectedRoute><PropertyManager /></ProtectedRoute>} />
            <Route path="/mortgage-calculator" element={<ProtectedRoute><MortgageCalculator /></ProtectedRoute>} />
            <Route path="/account-manager" element={<ProtectedRoute><AccountManager /></ProtectedRoute>} />
            <Route path="/taxable-accounts" element={<ProtectedRoute><TaxableAccounts /></ProtectedRoute>} />
            {/* Stock Analysis Routes */}
            <Route path="/individual-stock-analysis" element={<ProtectedRoute><IndividualStockAnalysis /></ProtectedRoute>} />
            <Route path="/bulk-stock-analysis" element={<BulkStockAnalysis />} />
            <Route path="/automated-trader" element={<ProtectedRoute><AutomatedTrader /></ProtectedRoute>} />
            <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />
            {/* Legacy routes for backwards compatibility */}
            <Route path="/stocks" element={<Navigate to="/individual-stock-analysis" replace />} />
            <Route path="/stockscreener" element={<Navigate to="/individual-stock-analysis" replace />} />
            
            {/* Public Contract Signature Route */}
            <Route path="/contract-signature/:verificationCode" element={<ContractSignature />} />
            
            {/* 404 Not Found Route */}
            <Route path="*" element={<NotFound />} />
          </Routes>
          </main>
        </div>
        
        {/* Toast Notifications */}
        <Toaster
          position="top-right"
          toastOptions={{
            duration: 4000,
            style: {
              background: '#363636',
              color: '#fff',
            },
            success: {
              duration: 3000,
              iconTheme: {
                primary: '#10b981',
                secondary: '#fff',
              },
            },
            error: {
              duration: 5000,
              iconTheme: {
                primary: '#ef4444',
                secondary: '#fff',
              },
            },
          }}
        />
      </div>
    </Router>
  );
}

function App() {
  return (
    <AuthProvider>
      <AppRoutes />
    </AuthProvider>
  );
}

export default App;
